<template>
  <Modal :show="show" :size="resolvedSize" :zIndex="zIndex" @close="emit('close')">
    <template #header>
      <div class="file-preview__header">
        <div class="file-preview__header-icon-wrap" :style="iconBadgeStyle">
          <Icon :icon="categoryIcon" class="file-preview__header-icon" />
        </div>
        <div class="file-preview__header-info">
          <div class="file-preview__header-name" :title="attachment?.fileName">
            {{ attachment?.fileName || "Preview" }}
          </div>
          <div v-if="attachment && attachment.fileSize != null" class="file-preview__header-size">
            {{ formatFileSize(attachment.fileSize) }}
          </div>
        </div>
      </div>
    </template>

    <div class="file-preview__body" :class="{ 'file-preview__body--audio': kind === 'audio' }">
      <div v-if="showFallback" class="file-preview__fallback">
        <Icon icon="mdi:file" class="file-preview__fallback-icon" />
        <p class="file-preview__fallback-text">{{ fallbackMessage }}</p>
      </div>

      <template v-else>
        <div v-if="kind === 'image'" class="file-preview__image">
          <img
            :src="attachment!.url"
            :alt="attachment!.fileName"
            class="file-preview__img"
            @error="mediaError = true"
          />
        </div>

        <div
          v-else-if="kind === 'pdf'"
          class="file-preview__pdf"
          ref="pdfContainer"
          :style="{ '--pdf-max-width': `${PDF_MAX_RENDER_WIDTH}px` }"
        >
          <VuePdfEmbed
            v-if="pdfRenderWidth != null"
            v-show="!pdfLoading"
            :source="attachment!.url"
            :width="pdfRenderWidth"
            class="file-preview__pdf-embed"
            @loaded="pdfLoading = false"
            @loading-failed="onPdfError"
          />
          <div v-if="pdfLoading" class="file-preview__pdf-loading">
            <Spinner size="lg" />
            <span class="file-preview__pdf-loading-text">Rendering PDF…</span>
          </div>
        </div>

        <template v-else-if="kind === 'text'">
          <div v-if="loadingText" class="file-preview__loading">
            <Spinner size="md" />
            <span>Loading preview...</span>
          </div>
          <pre v-else class="file-preview__text">{{ textContent }}</pre>
        </template>

        <audio
          v-else-if="kind === 'audio'"
          controls
          preload="metadata"
          :src="attachment!.url"
          class="file-preview__audio"
          @error="mediaError = true"
        />

        <video
          v-else-if="kind === 'video'"
          controls
          preload="metadata"
          :src="attachment!.url"
          class="file-preview__video"
          @error="mediaError = true"
        />
      </template>
    </div>

    <template v-if="attachment && attachment.url" #footer>
      <a :href="attachment.url" target="_blank" rel="noopener" class="file-preview__footer-link">
        <Button variant="secondary" size="sm">
          <Icon icon="mdi:open-in-new" />
          Open in new tab
        </Button>
      </a>
      <a
        :href="attachment.url"
        :download="attachment.fileName"
        target="_blank"
        rel="noopener"
        class="file-preview__footer-link"
      >
        <Button variant="primary" size="sm">
          <Icon icon="mdi:tray-arrow-down" />
          Download
        </Button>
      </a>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { Icon } from "@iconify/vue";
import VuePdfEmbed from "vue-pdf-embed";
import { Modal, Button, Spinner, type PreviewAttachment } from "@/components/ui";

interface Props {
  show: boolean;
  attachment: PreviewAttachment | null;
  size?: "md" | "lg" | "xl" | "xxl";
  zIndex?: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{ close: [] }>();

type Kind = "image" | "pdf" | "text" | "audio" | "video" | "unsupported";

const TEXT_SIZE_LIMIT = 1_048_576;

const kind = computed<Kind>(() => {
  const a = props.attachment;
  if (!a || !a.url) return "unsupported";
  const m = a.mimeType || "";
  if (m === "application/pdf") return "pdf";
  if (m === "text/plain") return "text";
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("video/")) return "video";
  return "unsupported";
});

const resolvedSize = computed<"md" | "lg" | "xl" | "xxl" | "sm">(() => {
  if (props.size) return props.size;
  if (kind.value === "pdf" || kind.value === "image" || kind.value === "video") return "xl";
  if (kind.value === "audio") return "sm";
  return "md";
});

const categoryIcon = computed(() => {
  switch (kind.value) {
    case "pdf":
      return "mdi:file-pdf-box";
    case "image":
      return "mdi:file-image-outline";
    case "audio":
      return "mdi:file-music-outline";
    case "video":
      return "mdi:file-video-outline";
    case "text":
      return "mdi:file-document-outline";
    default:
      return "mdi:file";
  }
});

const iconBadgeStyle = computed(() => {
  const map: Record<Kind, { bg: string; fg: string }> = {
    pdf: { bg: "#fee2e2", fg: "#dc2626" },
    image: { bg: "#d1fae5", fg: "#059669" },
    audio: { bg: "#ede9fe", fg: "#7c3aed" },
    video: { bg: "#ffedd5", fg: "#ea580c" },
    text: { bg: "#dbeafe", fg: "#2563eb" },
    unsupported: { bg: "#f3f4f6", fg: "#6b7280" },
  };
  const c = map[kind.value];
  return { backgroundColor: c.bg, color: c.fg };
});

const textContent = ref("");
const textTooLarge = ref(false);
const loadingText = ref(false);
const mediaError = ref(false);
const pdfLoading = ref(false);
const pdfContainer = ref<HTMLElement | null>(null);
const pdfRenderWidth = ref<number | undefined>(undefined);
let abortController: AbortController | null = null;
let pdfResizeObserver: ResizeObserver | null = null;

const PDF_MAX_RENDER_WIDTH = 600;

function measurePdfWidth() {
  const el = pdfContainer.value;
  if (!el) return;
  const styles = getComputedStyle(el);
  const padX = parseFloat(styles.paddingLeft || "0") + parseFloat(styles.paddingRight || "0");
  const inner = Math.max(0, el.clientWidth - padX);
  if (inner > 0) pdfRenderWidth.value = Math.round(Math.min(inner, PDF_MAX_RENDER_WIDTH));
}

function attachPdfResize() {
  if (!pdfContainer.value || pdfResizeObserver) return;
  measurePdfWidth();
  pdfResizeObserver = new ResizeObserver(() => measurePdfWidth());
  pdfResizeObserver.observe(pdfContainer.value);
}

function detachPdfResize() {
  if (pdfResizeObserver) {
    pdfResizeObserver.disconnect();
    pdfResizeObserver = null;
  }
}

function onPdfError() {
  pdfLoading.value = false;
  mediaError.value = true;
}

const showFallback = computed(() => {
  if (!props.attachment || !props.attachment.url) return true;
  if (kind.value === "unsupported") return true;
  if (mediaError.value) return true;
  if (kind.value === "text" && textTooLarge.value) return true;
  return false;
});

const fallbackMessage = computed(() => {
  if (kind.value === "text" && textTooLarge.value) return "File is too large to preview";
  if (mediaError.value) return "Preview failed to load";
  if (!props.attachment || !props.attachment.url) return "No file to preview";
  return "Preview not available for this file type";
});

function resetState() {
  textContent.value = "";
  textTooLarge.value = false;
  loadingText.value = false;
  mediaError.value = false;
  pdfLoading.value = false;
  pdfRenderWidth.value = undefined;
  detachPdfResize();
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}

async function loadText(url: string) {
  abortController = new AbortController();
  const signal = abortController.signal;
  loadingText.value = true;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const len = Number(res.headers.get("content-length") ?? "0");
    if (len > TEXT_SIZE_LIMIT) {
      textTooLarge.value = true;
      return;
    }
    const reader = res.body!.getReader();
    let total = 0;
    const chunks: Uint8Array[] = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > TEXT_SIZE_LIMIT) {
        textTooLarge.value = true;
        await reader.cancel();
        return;
      }
      chunks.push(value);
    }
    if (signal.aborted) return;
    textContent.value = await new Blob(chunks).text();
  } catch (err) {
    if ((err as DOMException).name !== "AbortError") mediaError.value = true;
  } finally {
    loadingText.value = false;
  }
}

watch(
  () => [props.show, props.attachment?.id] as const,
  ([show]) => {
    resetState();
    if (!show || !props.attachment?.url) return;
    if (kind.value === "text") loadText(props.attachment.url);
    if (kind.value === "pdf") {
      pdfLoading.value = true;
      // Modal is teleported to body, mount happens after this tick.
      nextTick(() => attachPdfResize());
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (abortController) abortController.abort();
  detachPdfResize();
});

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
</script>

<style scoped>
.file-preview__header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.file-preview__header-icon-wrap {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.file-preview__header-icon {
  width: 24px;
  height: 24px;
}

.file-preview__header-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
  flex: 1;
}

.file-preview__header-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-preview__header-size {
  font-size: 12px;
  color: #6b7280;
}

:deep(.ui-modal__body) {
  overflow: hidden;
}

.file-preview__body {
  width: 100%;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.file-preview__body--audio {
  min-height: 0;
  padding: 20px 16px;
}

.file-preview__image {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-preview__img {
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  border-radius: 8px;
}

.file-preview__pdf {
  position: relative;
  width: 100%;
  min-height: 480px;
  max-height: 75vh;
  overflow: auto;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}

.file-preview__pdf-embed {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: var(--pdf-max-width);
  margin-inline: auto;
}

.file-preview__pdf :deep(.vue-pdf-embed__page) {
  max-width: var(--pdf-max-width) !important;
  width: 100% !important;
  height: auto !important;
}

.file-preview__pdf :deep(canvas) {
  display: block;
  margin: 0 auto 12px;
  max-width: var(--pdf-max-width) !important;
  width: 100% !important;
  height: auto !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
}

.file-preview__pdf-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #ffffff;
  border-radius: 8px;
}
.file-preview__pdf-loading-text {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.file-preview__text {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  margin: 0;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
}

.file-preview__audio {
  width: 100%;
  max-width: 520px;
}

.file-preview__video {
  max-width: 100%;
  max-height: 75vh;
  border-radius: 8px;
  background: #000;
}

.file-preview__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #6b7280;
  padding: 48px;
}

.file-preview__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  text-align: center;
}

.file-preview__fallback-icon {
  width: 64px;
  height: 64px;
  color: #9ca3af;
}

.file-preview__fallback-text {
  font-size: 15px;
  color: #4b5563;
  margin: 0;
}

.file-preview__footer-link {
  text-decoration: none;
}
</style>
